<script setup lang="ts">
import { PURCHASE_LINKS } from "~/lib/book-purchase";

const { locale } = useI18n();

const showBrazilPricing = computed(() => locale.value === "pt-BR");
</script>

<template>
  <UtilsSection
    container-class="flex flex-col items-center max-w-screen-xl"
    id="buy-now"
    class="bg-gradient-to-b from-[#F1FBFE] to-white"
  >
    <div class="text-center mb-12">
      <UtilsTitle as="h2" class="text-4xl md:text-5xl" data-aos="fade-up">
        {{ $t("buy-now.button2") }}
      </UtilsTitle>
      <p class="text-xl text-gray-600 mt-3" data-aos="fade-up">
        {{ $t("buy.title") }}
      </p>
      <h3
        class="text-2xl md:text-3xl font-normal text-primary-foreground mt-2"
        data-aos="fade-up"
      >
        {{ $t("buy.subtitle") }}
      </h3>
    </div>

    <div
      class="grid gap-6 w-full"
      :class="showBrazilPricing ? 'md:grid-cols-2 xl:grid-cols-4' : 'md:grid-cols-2 max-w-3xl'"
    >
      <!-- Livrarias Curitiba -->
      <UtilsCard
        class="h-full"
        rounded="lg"
        data-aos="fade-up"
        containerClass="bg-white border-2 border-[#E31E24]/40 shadow-xl shadow-[#E31E24]/10 relative"
        wrapperClass="py-8"
      >
        <div
          class="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#E31E24] text-white text-xs font-bold uppercase tracking-wider px-4 py-1 rounded-full"
        >
          {{ $t("buy.livrarias-badge") }}
        </div>
        <div
          class="flex flex-col items-center justify-between h-full text-center"
        >
          <div>
            <img
              src="/images/partners/livrarias-curitiba.png"
              alt="Livrarias Curitiba"
              class="h-12 mx-auto mb-4 object-contain"
            />
            <div
              class="text-[#E31E24] font-neutron italic text-2xl md:text-3xl mb-6"
            >
              {{ $t("buy.livrarias-title") }}
            </div>
            <div
              class="h-px w-full bg-gradient-to-r from-transparent via-[#E31E24]/30 to-transparent mb-6"
            />
            <p class="text-lg font-medium text-gray-800">
              {{ $t("buy.printed-cover") }}
            </p>
            <p class="text-[#27C9F4] text-2xl italic font-neutron mt-2">
              429 {{ $t("buy.pages") }}
            </p>
            <div class="mt-6">
              <div class="text-sm text-gray-400">{{ $t("buy.for-only") }}</div>
              <div class="text-4xl font-semibold text-primary-foreground">
                R$ <span class="text-5xl">99</span>,90
              </div>
            </div>
          </div>
          <UtilsButton
            class="mt-8 w-full bg-[#E31E24] hover:bg-[#c91920]"
            as="a"
            :href="PURCHASE_LINKS.livrariasCuritiba"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ $t("store.button") }}
          </UtilsButton>
        </div>
      </UtilsCard>

      <!-- Impresso -->
      <UtilsCard
        v-if="showBrazilPricing"
        class="h-full"
        rounded="lg"
        data-aos="fade-up"
        :data-aos-delay="100"
        containerClass="bg-white border border-gray-100 shadow-lg shadow-gray-200/50"
        wrapperClass="py-8"
      >
        <div
          class="flex flex-col items-center justify-between h-full text-center"
        >
          <div>
            <div
              class="text-[#27C9F4] font-neutron italic text-3xl md:text-4xl mb-6"
            >
              {{ $t("buy.printed") }}
            </div>
            <div
              class="h-px w-full bg-gradient-to-r from-transparent via-[#21b3ed]/40 to-transparent mb-6"
            />
            <p class="text-lg font-medium text-gray-800">
              {{ $t("buy.printed-cover") }}
            </p>
            <p class="text-[#27C9F4] text-2xl italic font-neutron mt-2">
              429 {{ $t("buy.pages") }}
            </p>
            <div class="mt-6">
              <div class="text-sm text-gray-400">{{ $t("buy.for-only") }}</div>
              <div class="text-4xl font-semibold text-primary-foreground">
                R$ <span class="text-5xl">82</span>,10
              </div>
            </div>
          </div>
          <UtilsButton
            class="mt-8 w-full"
            as="a"
            :href="PURCHASE_LINKS.printedCheckout"
          >
            {{ $t("buy-now.button2") }}
          </UtilsButton>
        </div>
      </UtilsCard>

      <!-- E-book -->
      <UtilsCard
        v-if="showBrazilPricing"
        class="h-full"
        rounded="lg"
        data-aos="fade-up"
        :data-aos-delay="200"
        containerClass="bg-white border-2 border-[#27C9F4]/30 shadow-xl shadow-[#27C9F4]/10 relative"
        wrapperClass="py-8"
      >
        <div
          class="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FEE000] text-[#070d18] text-xs font-bold uppercase tracking-wider px-4 py-1 rounded-full"
        >
          {{ $t("buy.best-seller") }}
        </div>
        <div
          class="flex flex-col items-center justify-between h-full text-center"
        >
          <div>
            <div
              class="text-[#27C9F4] font-neutron italic text-3xl md:text-4xl mb-6"
            >
              {{ $t("buy.ebook") }}
            </div>
            <div
              class="h-px w-full bg-gradient-to-r from-transparent via-[#21b3ed]/40 to-transparent mb-6"
            />
            <p class="text-lg font-medium text-gray-800">
              {{ $t("buy.ebook-format") }}
            </p>
            <p class="text-[#27C9F4] text-xl italic font-neutron mt-2">
              {{ $t("buy.formats") }}
            </p>
            <div class="mt-6">
              <div class="text-sm text-gray-400">{{ $t("buy.for-only") }}</div>
              <div class="text-4xl font-semibold text-primary-foreground">
                R$ <span class="text-5xl">42</span>,67
              </div>
            </div>
          </div>
          <UtilsButton
            class="mt-8 w-full"
            as="a"
            :href="PURCHASE_LINKS.ebookCheckout"
          >
            {{ $t("buy-now.button2") }}
          </UtilsButton>
        </div>
      </UtilsCard>

      <!-- Amazon Internacional -->
      <UtilsCard
        class="h-full"
        rounded="lg"
        data-aos="fade-up"
        :data-aos-delay="showBrazilPricing ? 300 : 100"
        containerClass="bg-white border border-gray-100 shadow-lg shadow-gray-200/50"
        wrapperClass="py-8"
      >
        <div
          class="flex flex-col items-center justify-between h-full text-center"
        >
          <div>
            <SvgoAmazon
              :font-controlled="false"
              class="h-9 mx-auto mb-4"
              :filled="true"
            />
            <p class="text-xs uppercase tracking-widest text-gray-400 mb-4">
              {{ $t("buy.amazon-international") }}
            </p>
            <div
              class="h-px w-full bg-gradient-to-r from-transparent via-[#21b3ed]/40 to-transparent mb-6"
            />
            <p class="text-lg font-medium text-gray-800">
              {{ $t("buy.printed-cover") }}
            </p>
            <p class="text-[#27C9F4] text-2xl italic font-neutron mt-2">
              429 {{ $t("buy.pages") }}
            </p>
            <div class="flex items-center justify-center gap-2 mt-4">
              <img src="/images/flags/IT.svg" class="w-8 border rounded-sm" alt="" />
              <img src="/images/flags/UE.svg" class="w-8 border rounded-sm" alt="" />
              <img src="/images/flags/US.svg" class="w-8 border rounded-sm" alt="" />
            </div>
            <div class="mt-6">
              <div class="text-sm text-gray-400">{{ $t("buy.for-only") }}</div>
              <div class="text-4xl font-semibold text-primary-foreground">
                € <span class="text-5xl">19</span>,39
              </div>
            </div>
          </div>
          <UtilsButton
            class="mt-8 w-full"
            as="a"
            :href="PURCHASE_LINKS.amazonInternational"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ $t("buy-now.button2") }}
          </UtilsButton>
        </div>
      </UtilsCard>
    </div>
  </UtilsSection>
</template>
